import React, { useState } from 'react';
import { motion } from 'framer-motion';
import AddressAutocomplete from './AddressAutocomplete';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiUser, FiCalculator, FiInfo } = FiIcons;

const PropertyForm = ({ 
  onFormChange, 
  onAddressChange, 
  onCoordinatesChange, 
  onCalculate, 
  formData = {},
  address = '',
  canCalculate = false 
}) => {
  const [errors, setErrors] = useState({});
  const [showTooltip, setShowTooltip] = useState(null);

  const propertyTypes = [
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Appartamento' },
    { value: 'bnb', label: 'B&B' },
    { value: 'agriturismo', label: 'Agriturismo' },
    { value: 'loft', label: 'Loft' },
    { value: 'studio', label: 'Monolocale' },
    { value: 'holiday-home', label: 'Casa Vacanze' }
  ];

  const periodTypes = [
    { value: 'seasonal', label: 'Stagionale', desc: '4-6 mesi l\'anno' },
    { value: 'annual', label: 'Annuale', desc: 'Tutto l\'anno' }
  ];

  const conditions = [
    { value: 'ready', label: 'Pronta all\'uso' },
    { value: 'furnish', label: 'Da arredare' },
    { value: 'renovate', label: 'Da ristrutturare' },
    { value: 'buying', label: 'In acquisto' }
  ];

  const services = [
    { value: 'pool', label: 'Piscina' },
    { value: 'balcony', label: 'Balcone/Terrazza' },
    { value: 'jacuzzi', label: 'Jacuzzi' },
    { value: 'garden', label: 'Giardino/Corte' },
    { value: 'gym', label: 'Palestra' },
    { value: 'parking', label: 'Posto auto' },
    { value: 'garage', label: 'Garage' },
    { value: 'ac', label: 'Aria condizionata' },
    { value: 'elevator', label: 'Ascensore' },
    { value: 'view', label: 'Vista panoramica' },
    { value: 'pets', label: 'Animali ammessi' },
    { value: 'smartlock', label: 'Smart lock' }
  ];

  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    onFormChange(newFormData);
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleServicesChange = (serviceValue, checked) => {
    const currentServices = formData.services || [];
    const newServices = checked
      ? [...currentServices, serviceValue]
      : currentServices.filter(s => s !== serviceValue);
    
    handleChange('services', newServices);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.propertyType) newErrors.propertyType = 'Seleziona il tipo di proprietà';
    if (!address) newErrors.address = 'Inserisci l\'indirizzo';
    if (!formData.sqMeters || formData.sqMeters < 10) newErrors.sqMeters = 'Inserisci i metri quadrati (min 10)';
    if (!formData.rooms && formData.rooms !== 0) newErrors.rooms = 'Inserisci il numero di camere';
    if (!formData.bathrooms || formData.bathrooms < 1) newErrors.bathrooms = 'Inserisci il numero di bagni (min 1)';
    if (!formData.guests || formData.guests < 1) newErrors.guests = 'Inserisci il numero di ospiti (min 1)';
    if (!formData.periodType) newErrors.periodType = 'Seleziona il tipo di periodo';
    if (!formData.firstName) newErrors.firstName = 'Inserisci il nome';
    if (!formData.lastName) newErrors.lastName = 'Inserisci il cognome';
    if (!formData.phone) newErrors.phone = 'Inserisci il telefono';
    if (!formData.email) newErrors.email = 'Inserisci l\'email';
    if (!formData.gdprConsent) newErrors.gdprConsent = 'Devi accettare il trattamento dei dati';

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato email non valido';
    }

    if (formData.phone && !/^(\+39|0039|\+|00)?[0-9\s\-()]{8,15}$/.test(formData.phone)) {
      newErrors.phone = 'Formato telefono non valido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onCalculate();
    } else {
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
    }
  };

  const Tooltip = ({ content, field }) => (
    <div className="relative">
      <button
        type="button"
        onMouseEnter={() => setShowTooltip(field)}
        onMouseLeave={() => setShowTooltip(null)}
        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <SafeIcon icon={FiInfo} className="h-4 w-4" />
      </button>
      {showTooltip === field && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap z-10">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
        </div>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-lg p-6"
      >
        <div className="flex items-center mb-6">
          <SafeIcon icon={FiHome} className="h-6 w-6 mr-3" style={{ color: '#ff7f50' }} />
          <h2 className="text-xl font-bold" style={{ color: '#0b294b' }}>
            Dati immobile
          </h2>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
              Tipologia di proprietà *
            </label>
            <select
              name="propertyType"
              value={formData.propertyType || ''}
              onChange={(e) => handleChange('propertyType', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent outline-none transition-colors ${
                errors.propertyType ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
              }`}
            >
              <option value="">Seleziona tipologia</option>
              {propertyTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.propertyType && (
              <p className="mt-1 text-sm text-red-600">{errors.propertyType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
              Indirizzo completo *
            </label>
            <AddressAutocomplete
              value={address}
              onChange={onAddressChange}
              onCoordinatesChange={onCoordinatesChange}
              error={errors.address}
            />
            {errors.address && (
              <p className="mt-1 text-sm text-red-600">{errors.address}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="flex items-center text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
                Metri quadrati *
                <Tooltip content="Superficie calpestabile dell'immobile" field="sqMeters" />
              </label>
              <input
                type="number"
                name="sqMeters"
                min="10"
                max="1000"
                value={formData.sqMeters || ''}
                onChange={(e) => handleChange('sqMeters', parseInt(e.target.value) || '')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors ${
                  errors.sqMeters ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
                }`}
                placeholder="es. 80"
              />
              {errors.sqMeters && (
                <p className="mt-1 text-xs text-red-600">{errors.sqMeters}</p>
              )}
            </div>

            <div>
              <label className="flex items-center text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
                Camere *
                <Tooltip content="Numero di camere da letto" field="rooms" />
              </label>
              <input
                type="number"
                name="rooms"
                min="0"
                max="20"
                value={formData.rooms ?? ''}
                onChange={(e) => handleChange('rooms', parseInt(e.target.value) ?? '')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors ${
                  errors.rooms ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
                }`}
                placeholder="es. 2"
              />
              {errors.rooms && (
                <p className="mt-1 text-xs text-red-600">{errors.rooms}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
                Bagni *
              </label>
              <input
                type="number"
                name="bathrooms"
                min="1"
                max="20"
                value={formData.bathrooms || ''}
                onChange={(e) => handleChange('bathrooms', parseInt(e.target.value) || '')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors ${
                  errors.bathrooms ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
                }`}
                placeholder="es. 1"
              />
              {errors.bathrooms && (
                <p className="mt-1 text-xs text-red-600">{errors.bathrooms}</p>
              )}
            </div>

            <div>
              <label className="flex items-center text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
                Ospiti *
                <Tooltip content="Numero massimo di ospiti" field="guests" />
              </label>
              <input
                type="number"
                name="guests"
                min="1"
                max="30"
                value={formData.guests || ''}
                onChange={(e) => handleChange('guests', parseInt(e.target.value) || '')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors ${
                  errors.guests ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
                }`}
                placeholder="es. 4"
              />
              {errors.guests && (
                <p className="mt-1 text-xs text-red-600">{errors.guests}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-3" style={{ color: '#0b294b' }}>
              Tipologia periodo *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {periodTypes.map(period => (
                <label
                  key={period.value}
                  className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                    formData.periodType === period.value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-300 hover:border-orange-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="periodType"
                    value={period.value}
                    checked={formData.periodType === period.value}
                    onChange={(e) => handleChange('periodType', e.target.value)}
                    className="sr-only"
                  />
                  <div className="flex-1">
                    <div className="font-medium" style={{ color: '#0b294b' }}>
                      {period.label}
                    </div>
                    <div className="text-sm text-gray-600">
                      {period.desc}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {errors.periodType && (
              <p className="mt-1 text-sm text-red-600">{errors.periodType}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-3" style={{ color: '#0b294b' }}>
              Servizi extra
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {services.map(service => (
                <label
                  key={service.value}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.services?.includes(service.value) || false}
                    onChange={(e) => handleServicesChange(service.value, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-orange-200"
                    style={{ accentColor: '#ff7f50' }}
                  />
                  <span className="text-sm" style={{ color: '#0b294b' }}>
                    {service.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
              Condizioni proprietà
            </label>
            <select
              name="condition"
              value={formData.condition || 'ready'}
              onChange={(e) => handleChange('condition', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-transparent outline-none transition-colors"
            >
              {conditions.map(condition => (
                <option key={condition.value} value={condition.value}>
                  {condition.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-white rounded-2xl shadow-lg p-6"
      >
        <div className="flex items-center mb-6">
          <SafeIcon icon={FiUser} className="h-6 w-6 mr-3" style={{ color: '#ff7f50' }} />
          <h2 className="text-xl font-bold" style={{ color: '#0b294b' }}>
            I tuoi dati
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
              Nome *
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName || ''}
              onChange={(e) => handleChange('firstName', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent outline-none transition-colors ${
                errors.firstName ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
              }`}
              placeholder="Il tuo nome"
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
              Cognome *
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName || ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent outline-none transition-colors ${
                errors.lastName ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
              }`}
              placeholder="Il tuo cognome"
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
              Telefono *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent outline-colors ${
                errors.phone ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
              }`}
              placeholder="+39 123 456 7890"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#0b294b' }}>
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent outline-none transition-colors ${
                errors.email ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-200'
              }`}
              placeholder="tua.email@esempio.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="gdprConsent"
              checked={formData.gdprConsent || false}
              onChange={(e) => handleChange('gdprConsent', e.target.checked)}
              className={`mt-1 w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-orange-200 ${
                errors.gdprConsent ? 'border-red-500' : ''
              }`}
              style={{ accentColor: '#ff7f50' }}
            />
            <span className="text-sm" style={{ color: '#0b294b' }}>
              Autorizzo il trattamento dei dati personali per essere ricontattato per la consulenza richiesta, 
              in conformità al GDPR (Regolamento UE 2016/679). *
            </span>
          </label>
          {errors.gdprConsent && (
            <p className="mt-1 text-sm text-red-600">{errors.gdprConsent}</p>
          )}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <button
          type="submit"
          disabled={!canCalculate}
          className={`flex-1 flex items-center justify-center space-x-3 px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 ${
            canCalculate
              ? 'text-white hover:opacity-90'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed hover:scale-100 hover:shadow-lg'
          }`}
          style={{ 
            backgroundColor: canCalculate ? '#ff7f50' : undefined
          }}
        >
          <SafeIcon icon={FiCalculator} className="h-5 w-5" />
          <span>Calcola la stima</span>
        </button>

        <a
          href="#contatti"
          className="flex-1 sm:flex-initial flex items-center justify-center space-x-3 px-8 py-4 rounded-xl text-lg font-semibold border-2 transition-all duration-200 hover:bg-opacity-10"
          style={{ 
            color: '#ff7f50',
            borderColor: '#ff7f50',
            backgroundColor: 'transparent'
          }}
        >
          <SafeIcon icon={FiUser} className="h-5 w-5" />
          <span>Parla con un consulente</span>
        </a>
      </motion.div>
    </form>
  );
};

export default PropertyForm;